import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface DirectMessage {
  id: string;
  sender_id: string;
  recipient_id: string;
  message: string;
  attachments: any[];
  read_at: string | null;
  created_at: string;
  parent_id: string | null;
  reply_count: number;
  sender?: {
    id: string;
    full_name: string | null;
    email: string | null;
  };
  recipient?: {
    id: string;
    full_name: string | null;
    email: string | null;
  };
}

export interface Conversation {
  partnerId: string;
  partnerName: string;
  partnerEmail: string | null;
  lastMessage: string;
  lastMessageAt: string;
  unreadCount: number;
}

export const useDirectMessages = (partnerId?: string) => {
  const queryClient = useQueryClient();

  // Fetch all conversations (grouped by partner)
  const { data: conversations = [], isLoading: isLoadingConversations } = useQuery({
    queryKey: ["direct-conversations"],
    queryFn: async () => {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.user) throw new Error("Not authenticated");
      
      const userId = session.session.user.id;

      // Get all messages where user is sender or recipient
      const { data: messages, error } = await supabase
        .from("direct_messages")
        .select(`
          id,
          sender_id,
          recipient_id,
          message,
          read_at,
          created_at
        `)
        .or(`sender_id.eq.${userId},recipient_id.eq.${userId}`)
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Get unique partner IDs
      const partnerIds = new Set<string>();
      messages?.forEach(msg => {
        const partnerId = msg.sender_id === userId ? msg.recipient_id : msg.sender_id;
        partnerIds.add(partnerId);
      });

      // Fetch partner profiles
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, full_name, email")
        .in("id", Array.from(partnerIds));

      const profileMap = new Map(profiles?.map(p => [p.id, p]) || []);

      // Group messages by partner
      const conversationMap = new Map<string, Conversation>();
      
      messages?.forEach(msg => {
        const partnerId = msg.sender_id === userId ? msg.recipient_id : msg.sender_id;
        const partner = profileMap.get(partnerId);
        
        if (!conversationMap.has(partnerId)) {
          const unreadCount = messages?.filter(
            m => m.sender_id === partnerId && !m.read_at
          ).length || 0;

          conversationMap.set(partnerId, {
            partnerId,
            partnerName: partner?.full_name || partner?.email || "Unknown",
            partnerEmail: partner?.email || null,
            lastMessage: msg.message,
            lastMessageAt: msg.created_at,
            unreadCount,
          });
        }
      });

      return Array.from(conversationMap.values());
    },
  });

  // Fetch messages for a specific conversation
  const { data: messages = [], isLoading: isLoadingMessages } = useQuery({
    queryKey: ["direct-messages", partnerId],
    queryFn: async () => {
      if (!partnerId) return [];
      
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.user) throw new Error("Not authenticated");
      
      const userId = session.session.user.id;

      const { data, error } = await supabase
        .from("direct_messages")
        .select(`
          id,
          sender_id,
          recipient_id,
          message,
          attachments,
          read_at,
          created_at,
          parent_id,
          reply_count
        `)
        .or(`and(sender_id.eq.${userId},recipient_id.eq.${partnerId}),and(sender_id.eq.${partnerId},recipient_id.eq.${userId})`)
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Fetch sender/recipient profiles
      const userIds = new Set<string>();
      data?.forEach(msg => {
        userIds.add(msg.sender_id);
        userIds.add(msg.recipient_id);
      });

      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, full_name, email")
        .in("id", Array.from(userIds));

      const profileMap = new Map(profiles?.map(p => [p.id, p]) || []);

      return data?.map(msg => ({
        ...msg,
        sender: profileMap.get(msg.sender_id),
        recipient: profileMap.get(msg.recipient_id),
      })) as DirectMessage[];
    },
    enabled: !!partnerId,
  });

  // Send a message
  const { mutateAsync: sendMessage, isPending: isSending } = useMutation({
    mutationFn: async ({ 
      recipientId, 
      message,
      attachments,
    }: { 
      recipientId: string; 
      message: string;
      attachments?: Array<{ id: string; name: string; url: string; type: string; size: number }>;
    }) => {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("direct_messages")
        .insert({
          sender_id: session.session.user.id,
          recipient_id: recipientId,
          message,
          attachments: attachments || null,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["direct-messages", partnerId] });
      queryClient.invalidateQueries({ queryKey: ["direct-conversations"] });
    },
  });

  // Mark messages as read
  const { mutateAsync: markAsRead } = useMutation({
    mutationFn: async (messageIds: string[]) => {
      const { error } = await supabase
        .from("direct_messages")
        .update({ read_at: new Date().toISOString() })
        .in("id", messageIds);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["direct-messages", partnerId] });
      queryClient.invalidateQueries({ queryKey: ["direct-conversations"] });
    },
  });

  // Get total unread count
  const totalUnread = conversations.reduce((sum, c) => sum + c.unreadCount, 0);

  return {
    conversations,
    messages,
    isLoadingConversations,
    isLoadingMessages,
    sendMessage,
    isSending,
    markAsRead,
    totalUnread,
  };
};

// Hook for fetching all profiles (for starting new conversations)
export const useAllProfiles = () => {
  return useQuery({
    queryKey: ["all-profiles"],
    queryFn: async () => {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.user) throw new Error("Not authenticated");
      
      const { data, error } = await supabase
        .from("profiles")
        .select("id, full_name, email")
        .neq("id", session.session.user.id)
        .order("full_name");

      if (error) throw error;
      return data;
    },
  });
};
